import type { FileAlbum, FileItem, FileKind, FileView, LibDoc } from './types';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Mốc "lâu không dùng": sửa lần cuối trước 22/Oct/2025 (một năm trước ngày mẫu). */
export const STALE_BEFORE = '202510220000';
/** File lớn: trên 5 MB (sz tính KB). */
export const LARGE_KB = 5120;

/** yyyyMMddHHmm → dd/MMM/yyyy HH:mm */
export function fmtTs(ts: string): string {
  return `${ts.slice(6, 8)}/${MON[+ts.slice(4, 6) - 1]}/${ts.slice(0, 4)} ${ts.slice(8, 10)}:${ts.slice(10, 12)}`;
}
export const fmtDate = (ts: string): string => fmtTs(ts).slice(0, 11);

export function sizeLabel(kb: number): string {
  if (!kb) return '—';
  if (kb >= 1048576) return `${(kb / 1048576).toFixed(1).replace('.', ',')} GB`;
  if (kb >= 1024) return `${(kb / 1024).toFixed(1).replace('.', ',')} MB`;
  return `${kb} KB`;
}

/** Loại tệp theo đuôi: [mã chip, tên đầy đủ]. */
export function fileKind(name: string): [FileKind, string] {
  const e = (/\.([a-z0-9]+)$/i.exec(name)?.[1] ?? '').toLowerCase();
  if (e === 'pdf') return ['PDF', 'PDF'];
  if (/^docx?$/.test(e)) return ['DOC', 'Word'];
  if (/^(xlsx?|csv)$/.test(e)) return ['XLS', 'Excel'];
  if (/^pptx?$/.test(e)) return ['PPT', 'PowerPoint'];
  if (/^(jpe?g|png|heic|webp)$/.test(e)) return ['IMG', 'Ảnh'];
  if (/^(mp4|mov)$/.test(e)) return ['VID', 'Video'];
  return ['FILE', 'Tệp'];
}

/** Bỏ dấu + thường hoá để tìm. */
export const norm = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').toLowerCase();

/** Các mục của một góc nhìn; `mine` lấy theo thư mục `cur`. */
export function filesInView(all: readonly FileItem[], shared: readonly FileItem[], view: FileView, cur = 'root'): FileItem[] {
  const files = all.filter((f) => !f.dir);
  switch (view) {
    case 'mine': return all.filter((f) => f.parent === cur);
    case 'recent': return [...files].sort((a, b) => b.ts.localeCompare(a.ts)).slice(0, 10);
    case 'shared': return [...shared];
    case 'myshared': return all.filter((f) => f.sh);
    case 'large': return files.filter((f) => f.sz > LARGE_KB);
    case 'dupes': return files.filter((f) => f.dup || files.some((g) => g.dup === f.id));
    case 'stale': return files.filter((f) => f.ts < STALE_BEFORE);
    case 'external': return all.filter((f) => f.sh === 'anyone');
  }
}

/** Tìm theo tên + chữ trích, không dấu. */
export function searchFiles(items: readonly FileItem[], q: string): FileItem[] {
  const n = norm(q.trim());
  return n ? items.filter((f) => norm(`${f.n} ${f.txt ?? ''}`).includes(n)) : [...items];
}

export type SortKey = 'ts' | 'n' | 'sz';
/** Thư mục lên trước khi sắp theo tên; dir = 1 tăng, -1 giảm. */
export function sortFiles(items: readonly FileItem[], key: SortKey, dir: 1 | -1): FileItem[] {
  return [...items].sort((a, b) => {
    if (key === 'n' && !!a.dir !== !!b.dir) return a.dir ? -1 : 1;
    const va = key === 'sz' ? a.sz : key === 'n' ? norm(a.n) : a.ts;
    const vb = key === 'sz' ? b.sz : key === 'n' ? norm(b.n) : b.ts;
    return (va > vb ? 1 : va < vb ? -1 : 0) * dir;
  });
}

/** Tài liệu công ty còn dùng (chưa vào thùng rác). */
export const liveDocs = (docs: readonly LibDoc[]): LibDoc[] => docs.filter((d) => !d.deleted);
export const trashDocs = (docs: readonly LibDoc[]): LibDoc[] => docs.filter((d) => d.deleted);

export const DOC_TYPES = ['Form mẫu', 'LOI', 'Hợp đồng', 'Hóa đơn', 'Thông báo hãng tàu', 'Biểu phí', 'SOP-Quy trình', 'Nội quy', 'Công văn', 'Khác'] as const;
export const DOMAINS = ['Vận tải/Truck', 'CO', 'Kiểm dịch', 'Hải quan', 'Chứng từ/BL', 'Booking/Cước', 'Kho/Depot', 'HR/Nội bộ', 'Khác'] as const;

export interface LibFilter { trash: boolean; type: string | null; dom: string | null; q: string }
/** Tài liệu theo bộ lọc, mới nhất lên đầu; tìm trong tên, tóm tắt, chữ trích, thẻ. */
export function filterDocs(docs: readonly LibDoc[], f: LibFilter): LibDoc[] {
  const n = norm(f.q.trim());
  return (f.trash ? trashDocs(docs) : liveDocs(docs))
    .filter((d) => (!f.type || d.type === f.type) && (!f.dom || d.dom === f.dom))
    .filter((d) => !n || norm(`${d.title} ${d.n} ${d.sum} ${d.txt} ${d.tags.join(' ')}`).includes(n))
    .sort((a, b) => b.ts.localeCompare(a.ts));
}

export type AlbumRange = 'month' | '3m' | 'year' | 'all';
const RANGE_FROM: Record<AlbumRange, string> = { month: '20261001', '3m': '20260722', year: '20260101', all: '' };
/** Album trong khoảng ngày (mốc theo ngày mẫu 22/Oct/2026) + tìm tên, địa điểm, mô tả. */
export function filterAlbums(albums: readonly FileAlbum[], range: AlbumRange, q = ''): FileAlbum[] {
  const n = norm(q.trim());
  return albums.filter((a) => a.ts >= RANGE_FROM[range] && (!n || norm(`${a.n} ${a.loc} ${a.desc}`).includes(n)));
}
